export const ANALYTICS_EVENTS = {
  MARKET_VIEWED: "market.viewed",
  SIGNUP_COMPLETED: "auth.signup_completed",

  SETUP_STARTED: "setup.started",
  SETUP_STEP_VIEWED: "setup.step_viewed",
  SETUP_STEP_COMPLETED: "setup.step_completed",
  SETUP_STEP_VALIDATION_FAILED: "setup.step_validation_failed",
  SETUP_COMPLETED: "setup.completed",
  SETUP_ACTIVATION_COMPLETED: "setup.activation_completed",

  LEAD_FORM_VIEWED: "lead.form_viewed",
  LEAD_FORM_SUBMITTED: "lead.form_submitted",
  LEAD_FORM_STARTED: "lead.form_started",
  LEAD_FORM_STEP_VIEWED: "lead.form_step_viewed",
  LEAD_FORM_BLOCKED: "lead.form_blocked",
  LEAD_DRAFT_RECOVERED: "lead.draft_recovered",
  LEAD_CREATED: "lead.created",
  LEAD_THANK_YOU: "lead.thank_you",
  LEAD_CONTACTED: "lead.contacted",
  LEAD_QUOTED: "lead.quoted",
  LEAD_FOLLOW_UP: "lead.follow_up",
  LEAD_WON: "lead.won",
  LEAD_LOST: "lead.lost",

  LISTING_VIEWED: "listing.viewed",

  FAQ_QUESTION_ASKED: "faq.question_asked",
  FAQ_ANSWER_RATED: "faq.answer_rated",

  LISTING_CREATED: "listing.created",
  LISTING_SUBMISSION_VALIDATION_FAILED: "listing.submission_validation_failed",
  LISTING_SUBMITTED: "listing.submitted",

  QUOTE_SENT: "quote.sent",
  QUOTE_ACCEPTED: "quote.accepted",
  QUOTE_DECLINED: "quote.declined",
  QUOTE_OPENED: "quote.opened",
  QUOTE_DECLINE_STARTED: "quote.decline_started",
  PO_SUBMITTED: "po.submitted",
  APPROVAL_DECIDED: "approval.decided",

  CLAIM_GATE_SHOWN: "claim.gate_shown",
  CLAIM_SUBMITTED: "claim.submitted",
  CLAIM_CONFIRMED: "claim.confirmed",
  CLAIM_WITHDRAWN: "claim.withdrawn",

  CART_ITEM_ADDED: "cart.item_added",
  CART_ITEM_UPDATED: "cart.item_updated",
  CART_ITEM_REMOVED: "cart.item_removed",
  CART_VIEWED: "cart.viewed",
  CHECKOUT_BUY_NOW_CLICKED: "checkout.buy_now_clicked",
  CHECKOUT_STARTED: "checkout.started",
  CHECKOUT_REDIRECTED: "checkout.redirected",
  CHECKOUT_ADDRESS_ADDED: "checkout.address_added",
  CHECKOUT_QUOTE_SHOWN: "checkout.quote_shown",
  CHECKOUT_PURCHASE_CONFIRMED: "checkout.purchase_confirmed",
  CHECKOUT_BLOCKED: "checkout.blocked",
  CHECKOUT_SESSION_CREATED: "checkout.session_created",
  CHECKOUT_SESSION_CLOSED: "checkout.session_closed",
  CHECKOUT_PAYMENT_PAGE_LEFT: "checkout.payment_page_left",
  ORDER_CREATED: "order.created",
  ORDER_PLACED: "order.placed",
  ORDER_PAID: "order.paid",
  ORDER_PENDING_APPROVAL: "order.pending_approval",
  ORDER_APPROVED: "order.approved",
  ORDER_SHIPPED: "order.shipped",
  ORDER_DELIVERED: "order.delivered",
  ORDER_COMPLETED: "order.completed",
  ORDER_CANCELLED: "order.cancelled",
  ORDER_REFUNDED: "order.refunded",
  ORDER_PORTAL_VIEWED: "order.portal_viewed",

  CTA_CLICKED: "cta.clicked",
  TOOL_USED: "tool.used",
  ROUTING_NOT_FOUND: "routing.not_found",
  UI_ERROR_SHOWN: "ui.error_shown",
} as const;

export type AnalyticsEventName =
  (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];

export type LeadSource = "rfq" | "listing_quote" | "contact" | "web_lead";

export type ListingCreationSource =
  | "vendor_form"
  | "admin"
  | "public"
  | "variant"
  | "duplicate"
  | "import"
  | "quote_vendor"
  | "unknown";

export const LEGACY_EVENT_ALIASES: Partial<
  Record<AnalyticsEventName, string[]>
> = {
  [ANALYTICS_EVENTS.SIGNUP_COMPLETED]: [
    "signup_completed",
    "conversion.user_signed_up",
  ],
  [ANALYTICS_EVENTS.SETUP_STARTED]: ["setup_started"],
  [ANALYTICS_EVENTS.SETUP_STEP_VIEWED]: ["setup_step_viewed"],
  [ANALYTICS_EVENTS.SETUP_STEP_COMPLETED]: ["setup_step_completed"],
  [ANALYTICS_EVENTS.SETUP_COMPLETED]: ["setup_completed"],
  [ANALYTICS_EVENTS.SETUP_ACTIVATION_COMPLETED]: ["activation_completed"],
  [ANALYTICS_EVENTS.LEAD_FORM_VIEWED]: [
    "rfq.form_viewed",
    "listing_quote.form_viewed",
    "contact.form_viewed",
  ],
  [ANALYTICS_EVENTS.LEAD_FORM_SUBMITTED]: [
    "rfq.form_submitted",
    "listing_quote.submitted",
    "lead.form_submitted",
    "contact.form_submitted",
    "contact_form_submitted",
  ],
  [ANALYTICS_EVENTS.LEAD_FORM_BLOCKED]: ["rfq.form_blocked"],
  [ANALYTICS_EVENTS.LEAD_DRAFT_RECOVERED]: ["rfq.draft_recovered"],
  [ANALYTICS_EVENTS.LEAD_CREATED]: [
    "lead_submitted",
    "conversion.lead_submitted",
  ],
  [ANALYTICS_EVENTS.LEAD_THANK_YOU]: ["rfq.conversion"],
  [ANALYTICS_EVENTS.LISTING_CREATED]: ["product_upload.server_completed"],
  [ANALYTICS_EVENTS.LISTING_SUBMISSION_VALIDATION_FAILED]: [
    "product_upload.server_submission_validation_failed",
  ],
  [ANALYTICS_EVENTS.LISTING_SUBMITTED]: [
    "product_upload.server_submitted_for_review",
  ],
  [ANALYTICS_EVENTS.LEAD_CONTACTED]: ["lead_contacted"],
  [ANALYTICS_EVENTS.LEAD_QUOTED]: ["lead_quoted"],
  [ANALYTICS_EVENTS.LEAD_FOLLOW_UP]: ["lead_follow_up"],
  [ANALYTICS_EVENTS.LEAD_WON]: ["lead_won"],
  [ANALYTICS_EVENTS.LEAD_LOST]: ["lead_lost"],
};

export function eventNameUnion(canonical: AnalyticsEventName): string[] {
  return [canonical, ...(LEGACY_EVENT_ALIASES[canonical] ?? [])];
}
